import type {
  StrategyRadarError,
  StrategyRadarEntryConfirmation,
  StrategyRadarListingKind,
  StrategyRadarListingItem,
  StrategyRadarMarketItem,
  StrategyRadarResponse,
  StrategyRadarSectionStatus,
  StrategyRadarSignal
} from './types'
import { BSTOCK_REGISTRY, type BStockUniverseItem } from './strategyRadarModel.ts'

export type PublicStrategyRadarLoader = (
  signal?: AbortSignal
) => Promise<StrategyRadarSnapshot>

export type RadarSwingSignal = {
  state: StrategyRadarSignal['state']
  label: string
  tone: StrategyRadarSignal['tone']
  outlook: StrategyRadarSignal['outlook']
  price: number | null
  ema20: number | null
  ema50: number | null
  rsi14: number | null
  rangeLow: number | null
  rangeHigh: number | null
  entryRule: string
  exitRule: string
  reasons: string[]
  momentum: number[]
  dataTime: number | null
  latestPrice: number | null
  latestPriceAsOf: number | null
  trendForecast: StrategyRadarSignal['trend_forecast']
  forecastSummary: string
  forecastHorizon: StrategyRadarSignal['forecast_horizon']
  forecastConfidence: StrategyRadarSignal['forecast_confidence']
  positionTier: StrategyRadarSignal['position_tier']
  positionGuidance: string
}

export type RadarMarketResult = {
  symbol: string
  status: StrategyRadarMarketItem['status']
  change24hPct: number | null
  signal: RadarSwingSignal
  source: string | null
  cacheStatus: string
  isFallback: boolean
  warning: string | null
  errorCode: string | null
  btcZone: StrategyRadarMarketItem['btc_zone']
  btcZoneLabel: string | null
  btcZoneAction: string | null
}

export type RadarMarketBatch = {
  status: StrategyRadarSectionStatus
  items: RadarMarketResult[]
  requestedCount: number
  availableCount: number
  fetchedAt: number | null
  errors: StrategyRadarError[]
}

export type RadarListingResult = {
  code: string
  title: string
  releaseAt: number
  articleUrl: string
  tradingAt: number | null
  symbols: string[]
  status: StrategyRadarListingItem['status']
  warning: string | null
  kind: StrategyRadarListingKind
  kindLabel: string
  entryConfirmation: RadarEntryConfirmation
}

export type RadarEntryConfirmation = {
  state: StrategyRadarEntryConfirmation['state']
  label: string
  tone: StrategyRadarEntryConfirmation['tone']
  summary: string
  dataTime: number | null
}

export type RadarBStockUniverse = {
  status: 'available' | 'partial' | 'unavailable'
  items: BStockUniverseItem[]
  discoveredCount: number
  selectedCount: number
  limit: number
  truncated: boolean
  source: string | null
  fetchedAt: number | null
  warning: string | null
}

export type RadarListingBatch = {
  status: StrategyRadarSectionStatus
  items: RadarListingResult[]
  source: string | null
  cacheStatus: string
  fetchedAt: number | null
  errors: StrategyRadarError[]
}

export type StrategyRadarSnapshot = {
  methodologyVersion: string
  generatedAt: number | null
  venue: StrategyRadarResponse['venue']
  marketType: StrategyRadarResponse['market_type']
  timeframe: string
  researchOnly: boolean
  isTradeablePrice: boolean
  bStockUniverse: RadarBStockUniverse
  markets: RadarMarketBatch
  listings: RadarListingBatch
  errors: StrategyRadarError[]
}

export type StrategyRadarLoader = (
  refresh: boolean,
  signal?: AbortSignal
) => Promise<StrategyRadarResponse>

export async function loadStrategyRadar(
  backendEnabled: boolean,
  refresh: boolean,
  loader: StrategyRadarLoader,
  signal?: AbortSignal,
  publicLoader?: PublicStrategyRadarLoader
): Promise<StrategyRadarSnapshot | null> {
  if (!backendEnabled) {
    if (!publicLoader) throw new Error('静态策略雷达公开数据加载器未配置')
    return publicLoader(signal)
  }
  return adaptStrategyRadarResponse(await loader(refresh, signal))
}

export function adaptStrategyRadarResponse(response: StrategyRadarResponse): StrategyRadarSnapshot {
  return {
    methodologyVersion: response.methodology_version,
    generatedAt: timestamp(response.generated_at),
    venue: response.venue,
    marketType: response.market_type,
    timeframe: response.timeframe,
    researchOnly: response.research_only,
    isTradeablePrice: response.is_tradeable_price,
    bStockUniverse: registryUniverse(response.markets.items.map(item => item.symbol)),
    markets: {
      status: response.markets.status,
      items: response.markets.items.map(adaptMarketItem),
      requestedCount: response.markets.requested_count,
      availableCount: response.markets.available_count,
      fetchedAt: timestamp(response.markets.fetched_at),
      errors: response.markets.errors
    },
    listings: {
      status: response.listings.status,
      items: response.listings.items.map(adaptListingItem),
      source: response.listings.source,
      cacheStatus: response.listings.cache_status,
      fetchedAt: timestamp(response.listings.fetched_at),
      errors: response.listings.errors
    },
    errors: []
  }
}

function adaptMarketItem(item: StrategyRadarMarketItem): RadarMarketResult {
  return {
    symbol: item.symbol,
    status: item.status,
    change24hPct: finiteNumber(item.change_24h_pct),
    signal: adaptSignal(item.signal),
    source: item.source,
    cacheStatus: item.cache_status,
    isFallback: item.is_fallback,
    warning: item.warning,
    errorCode: item.error_code,
    btcZone: item.status === 'available' && !item.is_fallback && item.error_code == null
      ? validBtcZone(item.btc_zone)
      : null,
    btcZoneLabel: item.status === 'available' && !item.is_fallback && item.error_code == null
      ? nonEmptyString(item.btc_zone_label)
      : null,
    btcZoneAction: item.status === 'available' && !item.is_fallback && item.error_code == null
      ? nonEmptyString(item.btc_zone_action)
      : null
  }
}

function adaptListingItem(item: StrategyRadarListingItem): RadarListingResult {
  return {
    code: item.code,
    title: item.title,
    releaseAt: timestamp(item.release_at) ?? 0,
    articleUrl: item.article_url,
    tradingAt: timestamp(item.trading_at),
    symbols: item.symbols,
    status: item.status,
    warning: item.warning,
    kind: validListingKind(item.kind),
    kindLabel: nonEmptyString(item.kind_label) ?? '类型待核验',
    entryConfirmation: adaptEntryConfirmation(item.entry_confirmation)
  }
}

function adaptSignal(signal: StrategyRadarSignal | null | undefined): RadarSwingSignal {
  if (!signal) return unavailableSignal()
  return {
    state: signal.state,
    label: signal.label,
    tone: signal.tone,
    outlook: signal.outlook,
    price: finiteNumber(signal.price),
    ema20: finiteNumber(signal.ema20),
    ema50: finiteNumber(signal.ema50),
    rsi14: finiteNumber(signal.rsi14),
    rangeLow: finiteNumber(signal.range_low),
    rangeHigh: finiteNumber(signal.range_high),
    entryRule: signal.entry_rule,
    exitRule: signal.exit_rule,
    reasons: Array.isArray(signal.reasons) ? signal.reasons.filter(value => typeof value === 'string') : [],
    momentum: Array.isArray(signal.momentum)
      ? signal.momentum.flatMap(value => {
        const normalized = finiteNumber(value)
        return normalized == null ? [] : [Math.max(0, Math.min(100, normalized))]
      })
      : [],
    dataTime: timestamp(signal.data_time),
    latestPrice: finiteNumber(signal.latest_price),
    latestPriceAsOf: timestamp(signal.latest_price_as_of),
    trendForecast: validTrendForecast(signal.trend_forecast),
    forecastSummary: nonEmptyString(signal.forecast_summary) ?? '服务端未返回可核验趋势预测。',
    forecastHorizon: signal.forecast_horizon === 'NEXT_1_TO_3_CLOSED_4H'
      ? signal.forecast_horizon
      : 'NEXT_1_TO_3_CLOSED_4H',
    forecastConfidence: validForecastConfidence(signal.forecast_confidence),
    positionTier: validPositionTier(signal.position_tier),
    positionGuidance: nonEmptyString(signal.position_guidance) ?? '仓位数据待核验，不生成动作。'
  }
}

function timestamp(value: string | null | undefined): number | null {
  if (!value) return null
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : null
}

function finiteNumber(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

function validTrendForecast(value: unknown): RadarSwingSignal['trendForecast'] {
  return ['bullish', 'range', 'defensive'].includes(String(value))
    ? value as RadarSwingSignal['trendForecast']
    : 'unavailable'
}

function validForecastConfidence(value: unknown): RadarSwingSignal['forecastConfidence'] {
  return ['high', 'medium', 'low'].includes(String(value))
    ? value as RadarSwingSignal['forecastConfidence']
    : 'unavailable'
}

function validPositionTier(value: unknown): RadarSwingSignal['positionTier'] {
  return ['watch', 'small', 'reduce'].includes(String(value))
    ? value as RadarSwingSignal['positionTier']
    : 'none'
}

function validBtcZone(value: unknown): RadarMarketResult['btcZone'] {
  return ['below', 'accumulate', 'observe', 'reduce', 'breakout'].includes(String(value))
    ? value as RadarMarketResult['btcZone']
    : null
}

function validListingKind(value: unknown): StrategyRadarListingKind {
  return ['spot_listing', 'launchpool', 'launchpad', 'megadrop', 'airdrop', 'alpha'].includes(String(value))
    ? value as StrategyRadarListingKind
    : 'other'
}

function adaptEntryConfirmation(
  confirmation: StrategyRadarEntryConfirmation | null | undefined
): RadarEntryConfirmation {
  const state = confirmation?.state
  if (!confirmation || !['confirmed', 'waiting', 'blocked'].includes(String(state))) return unavailableEntryConfirmation()
  const tone = ['positive', 'warning', 'negative', 'neutral'].includes(String(confirmation.tone))
    ? confirmation.tone
    : 'neutral'
  return {
    state: confirmation.state,
    label: nonEmptyString(confirmation.label) ?? '1m 确认待核验',
    tone,
    summary: nonEmptyString(confirmation.summary) ?? '未返回可核验的 1m 确认说明。',
    dataTime: timestamp(confirmation.data_time)
  }
}

function unavailableEntryConfirmation(): RadarEntryConfirmation {
  return {
    state: 'unavailable',
    label: '1m 确认待核验',
    tone: 'neutral',
    summary: '未返回可核验的 1m 确认数据，不生成入场候选。',
    dataTime: null
  }
}

function registryUniverse(symbols: string[]): RadarBStockUniverse {
  const selected = new Set(symbols)
  const items: BStockUniverseItem[] = BSTOCK_REGISTRY
    .filter(item => selected.has(item.symbol))
    .map(item => ({
      symbol: item.symbol,
      token: item.token,
      fullName: `${item.company} (bStocks)`,
      company: item.company,
      underlying: item.underlying,
      theme: item.theme,
      volume: 0,
      registryStatus: 'registered'
    }))
  return {
    status: items.length ? 'partial' : 'unavailable',
    items,
    discoveredCount: 0,
    selectedCount: items.length,
    limit: 8,
    truncated: false,
    source: 'STATIC_OFFICIAL_REGISTRY',
    fetchedAt: null,
    warning: '服务端快照未携带动态 bStocks 发现结果，当前仅显示官方静态 registry。'
  }
}

function unavailableSignal(): RadarSwingSignal {
  return {
    state: 'unavailable',
    label: '数据待核验',
    tone: 'neutral',
    outlook: '待核验',
    price: null,
    ema20: null,
    ema50: null,
    rsi14: null,
    rangeLow: null,
    rangeHigh: null,
    entryRule: '服务端未返回可验证的 4H 规则信号。',
    exitRule: '数据不可用时不生成卖出或持有提示。',
    reasons: ['聚合信号不可用'],
    momentum: [],
    dataTime: null,
    latestPrice: null,
    latestPriceAsOf: null,
    trendForecast: 'unavailable',
    forecastSummary: '数据门禁未通过，不预测未来 1–3 根已闭合 4H。',
    forecastHorizon: 'NEXT_1_TO_3_CLOSED_4H',
    forecastConfidence: 'unavailable',
    positionTier: 'none',
    positionGuidance: '不生成仓位动作，等待数据恢复。'
  }
}
